/**
 * PortalShell — outer chrome (topbar + main + footer) for every page.
 *
 * Accessibility:
 *   - Skip-link for keyboard users → main content.
 *   - Semantic landmarks: <header>, <main id="main">, <footer>.
 *   - User chip only rendered when authenticated.
 *   - aria-current="page" on active nav link.
 */

import { NavLink, Link } from 'react-router';
import { useAuth } from '../../contexts/AuthContext';
import { LogOut } from '@retail-ops/panel-shell/fi';
import { Footer } from './Footer';

export function PortalShell({ children }: { children: React.ReactNode }): JSX.Element {
  const { isAuthenticated, identity, logout } = useAuth();
  const email = identity?.email ?? '';
  const initial = (identity?.displayName ?? email ?? '?').trim().charAt(0).toUpperCase();

  return (
    <div className="pcb-shell">
      <a href="#main" className="skip-link">Skip to main content</a>
      <header className="pcb-header" role="banner">
        <Link to={isAuthenticated ? '/dashboard' : '/'} className="pcb-wordmark" aria-label="Phelps County Bank — home">
          Phelps County Bank
          <span className="pcb-wordmark-sub">Member FDIC</span>
        </Link>
        {isAuthenticated ? (
          <nav className="pcb-header-nav" aria-label="Main">
            <NavLink to="/dashboard" end>Home</NavLink>
            <NavLink to="/inbox">Inbox</NavLink>
            <NavLink to="/preferences">Preferences</NavLink>
          </nav>
        ) : null}
        {isAuthenticated ? (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <div className="user-chip" aria-label={`Signed in as ${email || 'a customer'}`}>
              <span className="avatar" aria-hidden="true">{initial || '?'}</span>
              <span>{email || 'Customer'}</span>
            </div>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => void logout()}
              aria-label="Log out"
            >
              <LogOut aria-hidden="true" size={16} />
              <span className="visually-hidden">Log out</span>
            </button>
          </div>
        ) : (
          <div>
            <Link to="/auth/signin" className="btn btn-secondary">Sign in</Link>
          </div>
        )}
      </header>
      <main id="main" className="pcb-main" role="main">
        {children}
      </main>
      <Footer />
    </div>
  );
}
